import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Connects to data-controller="engage--checklist"
// Manages checklist panel — collapse, step completion, dismiss.
export default class extends Controller {
  static targets = [
    "panel",
    "steps",
    "step",
    "toggleBtn",
    "toggleIcon",
    "skipContainer",
  ]
  static values = {
    flowId: String,
    completedCount: Number,
    totalCount: Number,
  }

  #collapsed = false
  #abortController = null
  #completeTimer = null

  connect() {
    this.#abortController = new AbortController()
    void this.#startFlow()
  }

  disconnect() {
    this.#abortController?.abort()
    if (this.#completeTimer) clearTimeout(this.#completeTimer)
  }

  toggle() {
    this.#collapsed = !this.#collapsed
    this.stepsTarget.style.display = this.#collapsed ? "none" : ""
    this.toggleIconTarget.style.transform = this.#collapsed
      ? "rotate(-90deg)"
      : ""
  }

  async completeStep(event) {
    const stepKey = event.currentTarget.dataset.stepKey
    if (!stepKey) return

    // Optimistic UI — swap checkbox button for green tick
    const listItem = event.currentTarget.closest("[data-step-key]")
    if (listItem) this.#markStepCompleted(listItem)

    await this.#patch(
      `/api/v1/engage/flow_states/${this.flowIdValue}/complete_step`,
      {
        step_key: stepKey,
      },
    )

    this.completedCountValue += 1

    // All done → remove after short delay
    if (this.completedCountValue >= this.totalCountValue) {
      this.#completeTimer = setTimeout(() => this.element.remove(), 1200)
    }
  }

  async dismiss() {
    await this.#patch(`/api/v1/engage/flow_states/${this.flowIdValue}/dismiss`)
    this.element.remove()
  }

  // ── Private ──────────────────────────────────────────────────────────────

  async #startFlow() {
    await this.#patch(`/api/v1/engage/flow_states/${this.flowIdValue}/start`)
  }

  #markStepCompleted(listItem) {
    const btn = listItem.querySelector("button[data-action*='completeStep']")
    if (!btn) return

    const tick = document.createElement("span")
    tick.className =
      "flex h-5 w-5 items-center justify-center rounded-full bg-success/20"
    tick.innerHTML =
      '<svg class="h-3 w-3 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">' +
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>'
    btn.replaceWith(tick)

    const title = listItem.querySelector("p")
    if (title) title.classList.add("line-through", "text-foreground-muted")
  }

  async #patch(url, body = null) {
    try {
      const options = { credentials: "include", responseKind: "json" }
      if (this.#abortController) options.signal = this.#abortController.signal
      if (body) options.body = body
      const response = await patch(url, options)
      if (!response.ok)
        console.error(`[Engage] API error ${response.statusCode}`, url)
      return response
    } catch (e) {
      if (e.name !== "AbortError") throw e
    }
  }
}
